import { useState } from "react";
import { Form, Flex, Button, Col, Row, Upload, Modal, Spin } from "antd";
import { Input, Select } from "antd";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../../services/react-query/queryKeys";
import BarcodeSVG from "../../../assets/product/BarcodeSVG";
import { listBrand, listProductCategories } from "../../../network/products";
import {
  // MeasurementUnits,
  MeasurementUnitsEnum,
} from "../../../enum/productMeasure";
import { PlusOutlined } from "@ant-design/icons";
import AddProductCategory from "../addCategory/addCategory";
import AddBrand from "../addBrand/addBrand";
import { productType } from "../../../types/product";
import { UploadFile } from "antd/lib";
import { useIntl } from "react-intl";
import { checkStringInLanguage } from "../../../utils/utils";
import IMGUploader from "../../common/ui/IMGUploader";

interface InfoProps {
  onNextStep: () => void;
  onClose: () => void;
  product: productType;
  setProduct: React.Dispatch<React.SetStateAction<productType>>;
  productImg: UploadFile<any>;
  setProductImg: React.Dispatch<React.SetStateAction<UploadFile<any>>>;
  update: boolean;
}

function Info({
  onNextStep,
  onClose,
  product,
  setProduct,
  productImg,
  setProductImg,
  update,
}: InfoProps) {
  const [form] = Form.useForm();
  const [brandVisible, setBrandVisible] = useState(
    product?.brand_id ? false : false
  );
  const [categoryVisible, setCategoryVisible] = useState(
    product?.category_id ? false : false
  );
  const {
    data: listProductCategorie,
    refetch: refetchCategories,
    isLoading: isLoadingCategories,
  } = useQuery([queryKeys?.listProductCategories], () =>
    listProductCategories()
  );
  const {
    data: listBrands,
    refetch: refetchBrands,
    isLoading: isLoadingBrands,
  } = useQuery([queryKeys.listBrands], () => listBrand());
  const intl = useIntl();

  const MeasurementUnits = [
    {
      value: MeasurementUnitsEnum.Centimeters,
      label: intl.formatMessage({ id: "measurementUnits.centimeters" }),
    },
    {
      value: MeasurementUnitsEnum.Feet,
      label: intl.formatMessage({ id: "measurementUnits.feet" }),
    },
    {
      value: MeasurementUnitsEnum.Gallons,
      label: intl.formatMessage({ id: "measurementUnits.gallons" }),
    },
    {
      value: MeasurementUnitsEnum.Grams,
      label: intl.formatMessage({ id: "measurementUnits.grams" }),
    },
    {
      value: MeasurementUnitsEnum.Inches,
      label: intl.formatMessage({ id: "measurementUnits.inches" }),
    },
    {
      value: MeasurementUnitsEnum.Kilograms,
      label: intl.formatMessage({ id: "measurementUnits.kilograms" }),
    },
    {
      value: MeasurementUnitsEnum.Liters,
      label: intl.formatMessage({ id: "measurementUnits.liters" }),
    },
    {
      value: MeasurementUnitsEnum.Milliliters,
      label: intl.formatMessage({ id: "measurementUnits.milliliters" }),
    },
    {
      value: MeasurementUnitsEnum.Ounces,
      label: intl.formatMessage({ id: "measurementUnits.ounces" }),
    },
    {
      value: MeasurementUnitsEnum.Pounds,
      label: intl.formatMessage({ id: "measurementUnits.pounds" }),
    },
    {
      value: MeasurementUnitsEnum.Whole,
      label: intl.formatMessage({ id: "measurementUnits.whole" }),
    },
  ];
  const [fileList, setFileList] = useState<UploadFile[]>(
    productImg?.url || productImg?.originFileObj ? [productImg] : []
  );
  const onFinish = (values: {
    amount: string;
    measure: MeasurementUnitsEnum;
    category_id: number;
    brand_id: number;
    name: { ar: string; en: string };
    barcode: string;
    description: { ar: string; en: string };
    image: any;
  }) => {
    const newProduct = {
      ...product,
      name: {
        ar: values?.name?.ar,
        en: values?.name?.en,
      },
      category_id: values?.category_id,
      barcode: values?.barcode,
      description: {
        ar: values?.description?.ar,
        en: values?.description?.en,
      },
      amount: values?.amount,
      brand_id: values?.brand_id,
      measure: values?.measure,
    };

    setProduct(newProduct);
    onNextStep();
  };

  const measure = Form.useWatch("measure", form);
  const measureLabel = MeasurementUnits.find(
    (item) => item.value === measure
  )?.label;

  return (
    <div className="w-full">
      <AddBrand
        isOpen={brandVisible}
        handelModalClose={() => setBrandVisible(false)}
        setBrandId={(id: number) => {
          refetchBrands();
          if (id) {
            form.setFieldsValue({
              brand_id: id,
            });
          }
        }}
      />
      <AddProductCategory
        isOpen={categoryVisible}
        handelModalClose={() => setCategoryVisible(false)}
        setCategoryId={(id: number) => {
          refetchCategories();
          if (id) {
            form.setFieldsValue({
              category_id: id,
            });
          }
        }}
      />
      <Form
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={(changedValues: Partial<productType>, values: any) => {
          setProduct({
            ...product,
            ...changedValues,
            description: values?.description?.ar,
            name: values?.name,
          });
        }}
        form={form}
        initialValues={{
          measure: product?.measure || MeasurementUnitsEnum.Centimeters,
          name: product?.name,

          brand_id: product?.brand_id,
          category_id: product?.category_id,
          amount: product?.amount,
          description: product?.description,
          barcode: product?.barcode,
          image: product?.media,
        }}
      >
        <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 24 }}>
          <Col className="gutter-row h-full" xl={4} lg={6} xs={24} sm={24}>
            <IMGUploader
              name="image"
              fileList={fileList}
              setFileList={setFileList}
              maxCount={1}
              additionalOnChange={(fileList: UploadFile<any>[]) => {
                setProductImg(fileList[0]);
              }}
              imgValidation
            />
          </Col>
          <Col className="gutter-row" xl={20} lg={18} xs={24} sm={24}>
            <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
              <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
                <Form.Item
                  name={["name", "ar"]}
                  label={
                    intl.formatMessage({ id: "product_name_label" }) +
                    " " +
                    intl.formatMessage({ id: "inArabic" })
                  }
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "product_name_required_message",
                      }),
                    },
                    {
                      type: "string",
                      min: 2,
                      message: intl.formatMessage({
                        id: "product_name_length_message",
                      }),
                    },
                    {
                      validator: (_, value) => {
                        if (value) {
                          if (checkStringInLanguage(value, "ar")) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error(
                                intl.formatMessage({
                                  id: "service_name_arabic_error",
                                })
                              )
                            );
                          }
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "product_name_label",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
                <Form.Item
                  name={["name", "en"]}
                  label={
                    intl.formatMessage({ id: "product_name_label" }) +
                    " " +
                    intl.formatMessage({ id: "inEnglish" })
                  }
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "product_name_required_message",
                      }),
                    },
                    {
                      type: "string",
                      min: 2,
                      message: intl.formatMessage({
                        id: "product_name_length_message",
                      }),
                    },
                    {
                      validator: (_, value) => {
                        if (value) {
                          if (checkStringInLanguage(value, "en")) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error(
                                intl.formatMessage({
                                  id: "service_name_english_error",
                                })
                              )
                            );
                          }
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({
                      id: "product_name_label",
                    })}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
                <Form.Item
                  name="brand_id"
                  label={intl.formatMessage({ id: "brand_label" })}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "brand_required_message",
                      }),
                    },
                  ]}
                >
                  <Select
                    placeholder={intl.formatMessage({ id: "brand_label" })}
                    onChange={(value: number) => {
                      if (value === -1) {
                        setBrandVisible(true);
                      }
                    }}
                    popupClassName="select-custom-popup"
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Button
                          onClick={() => setBrandVisible(true)}
                          icon={<PlusOutlined />}
                          type="primary"
                          className="w-full btn-primary-light border-0"
                          style={{ justifyContent: "flex-start" }}
                          size="small"
                        >
                          {intl.formatMessage({ id: "add_brand_button_label" })}
                        </Button>
                      </>
                    )}
                    options={listBrands?.data?.map((item: any) => ({
                      value: item?.id,
                      label: item?.name?.ar || item?.name,
                    }))}
                    loading={isLoadingBrands}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
                <Form.Item
                  name="category_id"
                  label={intl.formatMessage({ id: "category_label" })}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "category_required_message",
                      }),
                    },
                  ]}
                >
                  <Select
                    placeholder={intl.formatMessage({ id: "category_label" })}
                    popupClassName="select-custom-popup"
                    onChange={(value: number) => {
                      if (value === -1) {
                        setCategoryVisible(true);
                      }
                    }}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Button
                          onClick={() => setCategoryVisible(true)}
                          icon={<PlusOutlined />}
                          type="primary"
                          className="w-full btn-primary-light border-0"
                          style={{ justifyContent: "flex-start" }}
                          size="small"
                        >
                          {intl.formatMessage({
                            id: "add_category_button_label",
                          })}
                        </Button>
                      </>
                    )}
                    options={listProductCategorie?.data?.map((item: any) => ({
                      value: item?.id,
                      label: item?.name?.ar || item?.name,
                    }))}
                    loading={isLoadingCategories}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={{ xs: 8, sm: 16, md: 8, lg: 14 }}>
              <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
                <Form.Item
                  name="barcode"
                  label={intl.formatMessage({ id: "barcode_label" })}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "barcode_required_message",
                      }),
                    },
                    {
                      max: 15,
                      message: intl.formatMessage({
                        id: "barcode_max_length_message",
                      }),
                    },
                  ]}
                >
                  <Input
                    placeholder={intl.formatMessage({ id: "barcode_label" })}
                    suffix={<BarcodeSVG />}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
                <Form.Item
                  name="measure"
                  label={intl.formatMessage({ id: "measurement_unit_label" })}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "measurement_unit_required_message",
                      }),
                    },
                  ]}
                >
                  <Select
                    placeholder={intl.formatMessage({
                      id: "measurement_unit_label",
                    })}
                    options={MeasurementUnits.map((item) => ({
                      value: item.value,
                      label: item.label,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" xl={8} lg={8} xs={24} sm={24}>
                <Form.Item
                  name="amount"
                  label={intl.formatMessage({ id: "size_label" })}
                  required={false}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "size_required_message",
                      }),
                    },
                    {
                      validator: (_, value) => {
                        if (value < 1) {
                          return Promise.reject(
                            new Error(
                              intl.formatMessage({
                                id: "size_min_value_message",
                              })
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="number"
                    min={1}
                    placeholder={intl.formatMessage({ id: "size_label" })}
                    suffix={
                      <span className="text-sm text-[#00086D]">
                        {measureLabel}
                      </span>
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 14 }}>
              <Col className="gutter-row" xl={24} lg={12} xs={24} sm={24}>
                <Form.Item
                  name={["description", "ar"]}
                  label={
                    intl.formatMessage({
                      id: "product_description_label",
                    })
                    // + ` (${intl.formatMessage({ id: "optional" })})`
                  }
                  className="w-full"
                  required={false}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: intl.formatMessage({
                  //       id: "product_description_required_message",
                  //     }),
                  //   },
                  // ]}
                >
                  <Input.TextArea
                    id="outlined-basic"
                    rows={3}
                    size="large"
                    placeholder={intl.formatMessage({
                      id: "product_description_placeholder",
                    })}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Flex gap="small" align="center" justify="center">
          <Button type="primary" block htmlType="submit">
            {intl.formatMessage({ id: "continue_button_label" })}
          </Button>
          <Button
            type="primary"
            onClick={onClose}
            className="btn-primary-light"
            block
            htmlType="button"
          >
            {intl.formatMessage({ id: "cancel_button_label" })}
          </Button>
        </Flex>
      </Form>
    </div>
  );
}

export default Info;
